import {forgetDeviceSession,cancelDeviceRestoration,keepDeviceSession} from '../services/deviceSession';
import {restAuthStorage} from '../firebase/restAuthStorage';
import {workspaceIdentity} from '../features/payday/workspaceStore';
import NotificationService from '../services/NotificationService';
import { readAuthProfile, writeAuthProfile, restoreSetupState } from '../firebase/authProfile';
import { signInWithGoogle, completeGoogleRedirect } from '../services/googleAuth';
import { editableProfileFields } from '../utils/profileFields';
import iosPaymentService from '../services/IOSPaymentService';
import androidPaymentService from '../services/AndroidPaymentService';
/* eslint-disable @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, signOut, onAuthStateChanged, createUserWithEmailAndPassword, updateProfile, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { FIRESTORE_BASE_URL, getIdToken } from '../firebase/firestore-rest';
import { UserProfile } from '../types';
import { apiFetch, buildApiUrl } from '../utils/api';
import { signInWithApple } from '../services/appleAuth';


const parseFirestoreTimestamp = (field: any, fallback?: Timestamp): Timestamp => {
  if (!field) {
    return fallback ?? Timestamp.now();
  }

  if (field.timestampValue) {
    const date = new Date(field.timestampValue);
    if (!Number.isNaN(date.getTime())) {
      return Timestamp.fromDate(date);
    }
  }

  if (field.stringValue) {
    const date = new Date(field.stringValue);
    if (!Number.isNaN(date.getTime())) {
      return Timestamp.fromDate(date);
    }
  }

  if (typeof field.seconds === 'number') {
    return new Timestamp(field.seconds, field.nanos ?? 0);
  }

  if (typeof field.integerValue === 'string') {
    const seconds = Number(field.integerValue);
    if (!Number.isNaN(seconds)) {
      return new Timestamp(seconds, 0);
    }
  }

  return fallback ?? Timestamp.now();
};

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  authLoading: boolean;
  signup: (email: string, password: string, name: string, ageRange: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<{ isNewUser: boolean }>;
  loginWithApple: () => Promise<{ isNewUser: boolean }>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  changeEmail: (newEmail: string, password: string) => Promise<{ success: boolean; message: string }>;
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  checkAutoLogout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUserState] = useState<User | null>(null);
  const setCurrentUser = (user:User|null) => { workspaceIdentity(user?.uid); setCurrentUserState(user); };
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Email/password authentication uses REST, so SDK-only listeners cannot keep
  // its premium badge current. Read the authoritative root record on every login.
  useEffect(() => {
    if (!currentUser?.uid) return;
    let active = true;
    const uid = currentUser.uid;
    const readAccess = async () => {
      try {
        const token = await getIdToken();
        const response = await fetch(`${FIRESTORE_BASE_URL}/users/${uid}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) return;
        const { fields } = await response.json();
        const subscription = fields?.subscription?.stringValue;
        if (!active || !['free', 'premium'].includes(subscription)) return;
        setUserProfile(previous => {
          if (!previous || previous.subscription === subscription) return previous;
          const updated = { ...previous, subscription };
          localStorage.setItem('finflow_user_profile', JSON.stringify(updated));
          return updated;
        });
      } catch (_) { /* Keep existing access when the network is unavailable. */ }
    };
    const reconcile = async () => {
      try {
        const isAppleApp = iosPaymentService.isAvailable();
        const isGoogleApp = androidPaymentService.isAvailable();
        if (isAppleApp) await iosPaymentService.setUserId(uid);
        if (isGoogleApp) await androidPaymentService.setUserId(uid);
        if (!active) return;
        await apiFetch(isAppleApp ? '/api/apple/verify-subscription' : isGoogleApp ? '/api/google/verify-subscription' : '/api/check-subscription-status', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid })
        });
      } catch (_) { /* Root record remains the fallback. */ }
      if (active) await readAccess();
    };
    void reconcile();
    const interval = window.setInterval(readAccess, 30000);
    const billingInterval = window.setInterval(reconcile, 5 * 60 * 1000);
    window.addEventListener('focus', reconcile);
    return () => { active = false; window.clearInterval(interval); window.clearInterval(billingInterval); window.removeEventListener('focus', reconcile); };
  }, [currentUser?.uid]);

  // Firebase authentication functions
  async function signup(email: string, password: string, name: string, ageRange: string) {
    cancelDeviceRestoration();
    try {
      console.log('🔍 [AuthContext] Starting signup for:', email);
      console.log('🔍 [AuthContext] Using Firebase REST API for signup...');
      
      // Use Firebase REST API for signup
      const response = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${auth.app.options.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'User-Agent': 'FinFlow-iOS/1.0'
        },
        body: JSON.stringify({
          email: email,
          password: password,
          returnSecureToken: true
        })
      });

      const data = await response.json();
      console.log('🔍 [AuthContext] REST API response received');
      
      if (data.error) {
        console.error('❌ Firebase REST API signup error:', data.error);
        if (data.error.message === 'EMAIL_EXISTS') {
          throw new Error('An account with this email already exists. Please sign in instead.');
        } else if (data.error.message === 'WEAK_PASSWORD') {
          throw new Error('Password should be at least 6 characters long.');
        } else if (data.error.message === 'INVALID_EMAIL') {
          throw new Error('Please enter a valid email address.');
        } else {
          throw new Error(data.error.message || 'Failed to create account');
        }
      }

      console.log('🔍 [AuthContext] User created successfully:', data.localId);
      
      // Store authentication data in localStorage
      const authData = {
        uid: data.localId,
        email: data.email,
        idToken: data.idToken,
        refreshToken: data.refreshToken,
        displayName: name,
        timestamp: Date.now()
      };
      restAuthStorage.setItem('finflow_auth', JSON.stringify(authData));
      
      // Create a Firebase-compatible user object
      const user = {
        uid: data.localId,
        email: data.email,
        displayName: name,
        photoURL: null,
        emailVerified: false,
        isAnonymous: false,
        metadata: {
          creationTime: new Date().toISOString(),
          lastSignInTime: new Date().toISOString()
        },
        providerData: [{
          uid: data.localId,
          email: data.email,
          displayName: name,
          photoURL: null,
          providerId: 'password'
        }],
        refreshToken: data.refreshToken,
        getIdToken: async () => data.idToken,
        getIdTokenResult: async () => ({
          authTime: new Date().toISOString(),
          issuedAtTime: new Date().toISOString(),
          signInProvider: 'password',
          token: data.idToken,
          claims: {}
        })
      };
      
      // Store the actual signup time - this should never change
      const actualSignupTime = Timestamp.now();
      
      const userProfile: UserProfile = {
        name,
        email,
        ageRange,
        createdAt: actualSignupTime, // This is the REAL account creation time
        subscription: 'free',
        hasOnboarded: false,
        hasSeenOnboardingPrompt: false,
      };
      
      console.log('🔍 [AuthContext] Setting user profile:', userProfile);
      setUserProfile(userProfile);
      
      // Create profile in background (don't wait for it)
      setDoc(doc(db, 'users', data.localId, 'profile', 'main'), editableProfileFields(userProfile), { merge: true }).catch(error => {
        console.error('Failed to create user profile:', error);
      });
      // Also create/seed the root user doc so top-level users streams stay complete.
      setDoc(doc(db, 'users', data.localId), {
        email,
        createdAt: actualSignupTime,
        hasOnboarded: false,
        hasSeenOnboardingPrompt: false
      }, { merge: true }).catch(error => {
        console.error('Failed to seed root user doc:', error);
      });
      
      // Set current user immediately
      setCurrentUser(user as any);
      await keepDeviceSession(user as User);
      // Clear loading immediately so UI can proceed (esp. on iOS REST signup)
      setAuthLoading(false);
      
      console.log('🔍 [AuthContext] Signup completed successfully');
    } catch (error: any) {
      console.error('❌ [AuthContext] Signup error:', error);
      console.error('❌ [AuthContext] Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw new Error(error.message);
    }
  }

  async function login(email: string, password: string) {
    cancelDeviceRestoration();
    try {
      // Use direct Firebase REST API
      const response = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${auth.app.options.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'User-Agent': 'FinFlow-iOS/1.0'
        },
        body: JSON.stringify({
          email: email,
          password: password,
          returnSecureToken: true
        })
      });

      const data = await response.json();
      if (data.error) {
        console.error('❌ Firebase REST API error:', data.error);
        if (data.error.message === 'INVALID_LOGIN_CREDENTIALS') {
          throw new Error('Invalid email or password. Please check your credentials and try again.');
        } else if (data.error.message === 'USER_NOT_FOUND') {
          throw new Error('No account found with this email. Please check your email or create a new account.');
        } else if (data.error.message === 'TOO_MANY_ATTEMPTS_TRY_LATER') {
          throw new Error('Too many login attempts. Please wait a few minutes before trying again.');
        } else {
          throw new Error(data.error.message || 'Authentication failed');
        }
      }

      if (data.idToken) {
        // Create a Firebase-compatible user object
        const user = {
          uid: data.localId,
          email: data.email,
          displayName: data.displayName || null,
          photoURL: null,
          emailVerified: false,
          isAnonymous: false,
          metadata: {
            creationTime: new Date().toISOString(),
            lastSignInTime: new Date().toISOString()
          },
          providerData: [{
            uid: data.localId,
            email: data.email,
            displayName: data.displayName || null,
            photoURL: null,
            providerId: 'password'
          }],
          refreshToken: data.refreshToken,
          getIdToken: async () => data.idToken,
          getIdTokenResult: async () => ({
            authTime: new Date().toISOString(),
            issuedAtTime: new Date().toISOString(),
            signInProvider: 'password',
            token: data.idToken,
            claims: {}
          })
        };
        
        setCurrentUser(user as any);
        
        // Store authentication data in localStorage (consistent with signup)
        const authData = {
          uid: data.localId,
          email: data.email,
          idToken: data.idToken,
          refreshToken: data.refreshToken,
          displayName: data.displayName || null,
          timestamp: Date.now()
        };
        restAuthStorage.setItem('finflow_auth', JSON.stringify(authData));
        await keepDeviceSession(user as User);
        // Fetch user profile immediately after successful login
        try {
          const firestoreResponse = await fetch(`${FIRESTORE_BASE_URL}/users/${data.localId}/profile/main?key=${auth.app.options.apiKey}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${data.idToken}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (firestoreResponse.ok) {
            const profileData = await firestoreResponse.json();
            if (profileData.fields) {
              // Convert Firestore document to UserProfile
              const profileFields = profileData.fields;
              const rootResponse = await fetch(`${FIRESTORE_BASE_URL}/users/${data.localId}`, {
                headers: { Authorization: `Bearer ${data.idToken}` }
              });
              if (rootResponse.ok) {
                const root = (await rootResponse.json()).fields;
                for (const key of ['hasOnboarded', 'hasSeenOnboardingPrompt']) {
                  if (profileFields[key]?.booleanValue === undefined && root?.[key]?.booleanValue !== undefined) profileFields[key] = root[key];
                }
                if (root?.subscription) profileFields.subscription = root.subscription;
                if (root?.subscriptionProvider) profileFields.subscriptionProvider = root.subscriptionProvider;
                if (root?.stripeCustomerId) profileFields.stripeCustomerId = root.stripeCustomerId;
              }
              const createdAt = parseFirestoreTimestamp(profileFields.createdAt, Timestamp.now());
              const trialUsedAt = profileFields.trialUsedAt ? parseFirestoreTimestamp(profileFields.trialUsedAt) : undefined;
              const trialStart = profileFields.trialStart ? parseFirestoreTimestamp(profileFields.trialStart) : undefined;

              const userProfile: UserProfile = {
                name: profileFields.name?.stringValue || data.email.split('@')[0],
                email: data.email,
                ageRange: profileFields.ageRange?.stringValue || '18-24',
                createdAt,
                hasOnboarded: profileFields.hasOnboarded?.booleanValue !== false,
                hasSeenOnboardingPrompt: profileFields.hasSeenOnboardingPrompt?.booleanValue ?? false,
                subscription: profileFields.subscription?.stringValue || 'free',
                subscriptionProvider: profileFields.subscriptionProvider?.stringValue,
                stripeCustomerId: profileFields.stripeCustomerId?.stringValue,
                hasUsedTrial: profileFields.hasUsedTrial?.booleanValue,
                trialUsedAt,
                trialStart,
                accountStatus: profileFields.accountStatus?.stringValue || 'active',
                deletedAt: profileFields.deletedAt?.stringValue,
                reactivationDeadline: profileFields.reactivationDeadline?.stringValue,
                canReactivate: profileFields.canReactivate?.booleanValue,
                reactivatedAt: profileFields.reactivatedAt?.stringValue,
                dataArchived: profileFields.dataArchived?.booleanValue,
                archivedAt: profileFields.archivedAt?.stringValue,
                archiveId: profileFields.archiveId?.stringValue
              };

              // Check if account was deleted and handle reactivation
              if (userProfile.accountStatus === 'deleted' && userProfile.canReactivate) {
                const now = new Date();
                const deadline = userProfile.reactivationDeadline ? new Date(userProfile.reactivationDeadline) : null;
                
                if (deadline && now < deadline) {
                  // Account can be reactivated
                  try {
                    // Reactivate the account
                    const reactivationResponse = await fetch(`${FIRESTORE_BASE_URL}/users/${data.localId}/profile/main?key=${auth.app.options.apiKey}`, {
                      method: 'PATCH',
                      headers: {
                        'Authorization': `Bearer ${data.idToken}`,
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({
                        fields: {
                          accountStatus: { stringValue: 'active' },
                          canReactivate: { booleanValue: false },
                          reactivatedAt: { stringValue: now.toISOString() }
                        }
                      })
                    });

                    if (reactivationResponse.ok) {
                      // Update local profile
                      userProfile.accountStatus = 'active';
                      userProfile.canReactivate = false;
                      userProfile.reactivatedAt = now.toISOString();
                      
                      // Show reactivation success message
                      alert(
                        '🎉 Welcome back! Your account has been reactivated successfully!\n\n' +
                        'All your data has been restored and you can continue using FinFlow as normal.'
                      );
                    }
                  } catch (reactivationError) {
                    console.error('Failed to reactivate account:', reactivationError);
                    // Continue with login even if reactivation fails
                  }
                } else if (deadline && now >= deadline) {
                  // Reactivation period has expired
                  throw new Error(
                    '❌ Account reactivation period has expired!\n\n' +
                    'Your account was deleted on ' + new Date(userProfile.deletedAt || '').toLocaleDateString() + '\n' +
                    'The 7-day reactivation period ended on ' + deadline.toLocaleDateString() + '\n\n' +
                    'Your data has been permanently deleted and cannot be recovered.\n' +
                    'Please contact support if you need assistance.'
                  );
                }
              }

              setUserProfile(userProfile);
              } else {
              // Missing profile at login usually indicates a legacy account.
              const defaultProfile: UserProfile = {
                name: data.email.split('@')[0],
                email: data.email,
                ageRange: '18-24',
                createdAt: Timestamp.now(),
                hasOnboarded: true,
                hasSeenOnboardingPrompt: true,
                subscription: 'free'
              };
              setUserProfile(defaultProfile);
              setDoc(doc(db, 'users', data.localId, 'profile', 'main'), editableProfileFields(defaultProfile), { merge: true }).catch(error => {
                console.error('Failed to create missing profile after login:', error);
              });
              setDoc(doc(db, 'users', data.localId), {
                email: data.email,
                hasOnboarded: defaultProfile.hasOnboarded,
                hasSeenOnboardingPrompt: defaultProfile.hasSeenOnboardingPrompt
              }, { merge: true }).catch(error => {
                console.error('Failed to seed root user doc after login:', error);
              });
            }
          } else {
            const errorText = await firestoreResponse.text();
            const defaultProfile: UserProfile = {
              name: data.email.split('@')[0],
              email: data.email,
              ageRange: '18-24',
              createdAt: Timestamp.now(),
              hasOnboarded: true,
              hasSeenOnboardingPrompt: true,
              subscription: 'free'
            };
            setUserProfile(defaultProfile);
            }
        } catch (firestoreError: any) {
          const defaultProfile: UserProfile = {
            name: data.email.split('@')[0],
            email: data.email,
            ageRange: '18-24',
            createdAt: Timestamp.now(),
            hasOnboarded: true,
            hasSeenOnboardingPrompt: true,
            subscription: 'free'
          };
          setUserProfile(defaultProfile);
          }
        
        setAuthLoading(false);
        } else {
        throw new Error('No authentication token received');
      }
    } catch (error: any) {
      console.error('❌ REST API login failed:', error);
      if (error.message.includes('fetch')) {
        throw new Error('Network connection failed. Please check your internet connection and try again.');
      }
      throw new Error(error.message || 'Authentication failed. Please try again.');
    }
  }

  async function loginWithGoogle(): Promise<{ isNewUser: boolean }> {
    cancelDeviceRestoration();
    try {
      const result = await signInWithGoogle(auth);
      if(!result?.user)return { isNewUser: false };
      await keepDeviceSession(result.user);
      const user = result.user;
      const isNewUser = Boolean((result as any)?.additionalUserInfo?.isNewUser);

      const profileRef = doc(db, 'users', user.uid, 'profile', 'main');
      const profileSnap = await readAuthProfile(user);
      if (!profileSnap.exists()) {
        const profile: UserProfile = {
          name: user.displayName || user.email?.split('@')[0] || 'User',
          email: user.email || '',
          ageRange: '18-24',
          createdAt: Timestamp.now(),
          hasOnboarded: false,
          hasSeenOnboardingPrompt: false,
          subscription: 'free'
        };
        await writeAuthProfile(user, editableProfileFields(profile));
        await writeAuthProfile(user, {
          email: profile.email,
          createdAt: profile.createdAt,
            hasOnboarded: profile.hasOnboarded,
          hasSeenOnboardingPrompt: profile.hasSeenOnboardingPrompt
        }, true);
        localStorage.setItem('finflow_user_profile', JSON.stringify(profile));
        setUserProfile(profile);
      }

      await fetchUserProfile(user);
      setCurrentUser(user);
      setAuthLoading(false);
      return { isNewUser };
    } catch (error: any) {
      if (error?.code === 'auth/popup-closed-by-user') {
        throw new Error('Google sign-in was canceled.');
      }
      if (error?.code === 'auth/popup-blocked') {
        throw new Error('Popup blocked. Please allow popups and try again.');
      }
      throw new Error(error?.message || 'Google sign-in failed');
    }
  }

  async function loginWithApple(): Promise<{ isNewUser: boolean }> {
    cancelDeviceRestoration();
    const { user } = await signInWithApple();
    await keepDeviceSession(user);
    restAuthStorage.removeItem('finflow_auth');
    const profileRef = doc(db, 'users', user.uid, 'profile', 'main');
    const snapshot = await getDoc(profileRef);
    const isNewUser = !snapshot.exists();
    const profile = snapshot.exists() ? snapshot.data() as UserProfile : {
      name: user.displayName || 'FinFlow member',
      email: user.email || '',
      ageRange: '',
      createdAt: Timestamp.now(),
      hasOnboarded: false,
      hasSeenOnboardingPrompt: false,
      subscription: 'free' as const
    };
    if (isNewUser) {
      await setDoc(profileRef, editableProfileFields(profile), { merge: true });
      await setDoc(doc(db, 'users', user.uid), {
        email: profile.email, createdAt: profile.createdAt,
        hasOnboarded: false, hasSeenOnboardingPrompt: false
      }, { merge: true });
    }
    localStorage.setItem('finflow_user_profile', JSON.stringify(profile));
    await fetchUserProfile(user);
    setCurrentUser(user);
    setAuthLoading(false);
    return { isNewUser };
  }

  async function logout(forgetDevice = true) {
    try {
      cancelDeviceRestoration();
      if(forgetDevice)await forgetDeviceSession();
      const notificationCleanup=NotificationService.clearUser().catch(() => {});
      // Clear authentication data from localStorage
      restAuthStorage.removeItem('finflow_auth');
      localStorage.removeItem('finflow_session'); // Also clear old session data
      
      // Clear AI insights session data
      sessionStorage.removeItem('aiInsightsLoaded');
      
      // Clear local state
      setUserProfile(null);
      setCurrentUser(null);
      setAuthLoading(false);
      
      // Try to sign out from Firebase (might fail with REST API approach)
      try {
        await signOut(auth);
      } catch (firebaseError) {
        // Firebase signout failed, but that's okay with REST API approach
      }
      await notificationCleanup;
    } catch (error: any) {
      console.error('❌ Logout error:', error);
      throw new Error(error.message);
    }
  }

  async function updateUserProfile(updates: Partial<UserProfile>) {
    updates = editableProfileFields(updates);
    if (!currentUser) throw new Error('No user logged in');
    
    try {
      // Check if email is being updated
      if (updates.email && updates.email !== currentUser.email) {
        // Email is being changed - this requires special handling
        throw new Error('Email address cannot be changed directly. Please use the "Change Email" option in Settings for secure email updates.');
      }
      
      // Update the profile subdocument
      await writeAuthProfile(currentUser, updates);
      
      // Mirror key profile fields into the root user document for consistency.
      if (
        updates.subscription !== undefined ||
        updates.stripeCustomerId !== undefined ||
        updates.hasOnboarded !== undefined ||
        updates.hasSeenOnboardingPrompt !== undefined ||
        updates.onboardingProgress !== undefined
      ) {
        const rootUpdates: any = {};
        if (updates.subscription !== undefined) rootUpdates.subscription = updates.subscription;
        if (updates.subscriptionProvider !== undefined) rootUpdates.subscriptionProvider = updates.subscriptionProvider;
        if (updates.stripeCustomerId !== undefined) rootUpdates.stripeCustomerId = updates.stripeCustomerId;
        if (updates.onboardingProgress !== undefined) rootUpdates.onboardingProgress = updates.onboardingProgress;
        if (updates.hasOnboarded !== undefined) rootUpdates.hasOnboarded = updates.hasOnboarded;
        if (updates.hasSeenOnboardingPrompt !== undefined) rootUpdates.hasSeenOnboardingPrompt = updates.hasSeenOnboardingPrompt;
        await writeAuthProfile(currentUser, rootUpdates, true);
      }
      
      // Update both state and localStorage to prevent profile loss
      setUserProfile(prev => {
        const updatedProfile = prev ? { ...prev, ...updates } : null;
        if (updatedProfile) {
          localStorage.setItem('finflow_user_profile', JSON.stringify(updatedProfile));
        }
        return updatedProfile;
      });
    } catch (error: any) {
      throw new Error(error.message);
    }
  }

  // Function to change email address with proper verification using REST API
  async function changeEmail(newEmail: string, password: string) {
    if (!currentUser) throw new Error('No user logged in');
    
    try {
      // First, verify the current password by attempting to sign in
      const verifyResponse = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${auth.app.options.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'User-Agent': 'FinFlow-iOS/1.0'
        },
        body: JSON.stringify({
          email: currentUser.email,
          password: password,
          returnSecureToken: true
        })
      });

      const verifyData = await verifyResponse.json();
      if (verifyData.error) {
        if (verifyData.error.message === 'INVALID_LOGIN_CREDENTIALS') {
          throw new Error('Incorrect password. Please try again.');
        } else {
          throw new Error('Failed to verify current password. Please try again.');
        }
      }

      // Now attempt to update the email using the REST API
      // Note: Firebase REST API doesn't support email updates directly
      // We need to inform the user about this limitation
      return { 
        success: false, 
        message: 'Email address changes are not supported in the current version. Please contact support for assistance.' 
      };
      
      // The following code would work if Firebase REST API supported email updates:
      /*
      const updateResponse = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:update?key=${auth.app.options.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'User-Agent': 'FinFlow-iOS/1.0'
        },
        body: JSON.stringify({
          idToken: verifyData.idToken,
          email: newEmail,
          returnSecureToken: true
        })
      });

      const updateData = await updateResponse.json();
      if (updateData.error) {
        throw new Error(`Failed to update email: ${updateData.error.message}`);
      }

      // Update the profile document with the new email
      const userRef = doc(db, 'users', currentUser.uid, 'profile', 'main');
      await setDoc(userRef, { email: newEmail }, { merge: true });
      
      // Update local state
      setUserProfile(prev => {
        if (prev) {
          const updatedProfile = { ...prev, email: newEmail };
          localStorage.setItem('finflow_user_profile', JSON.stringify(updatedProfile));
          return updatedProfile;
        }
        return null;
      });
      
      return { success: true, message: 'Email updated successfully! Please check your new email for verification.' };
      */
    } catch (error: any) {
      console.error('Failed to change email:', error);
      throw error;
    }
  }

  async function refreshUserProfile() {
    if (!currentUser) return;
    try {
      await fetchUserProfile(currentUser);
      const response = await fetch(`${FIRESTORE_BASE_URL}/users/${currentUser.uid}`, {
        headers: { Authorization: `Bearer ${await getIdToken()}` }
      });
      if (response.ok) {
        const fields = (await response.json()).fields;
        if (['free', 'premium'].includes(fields?.subscription?.stringValue)) {
          setUserProfile(previous => {
            if (!previous) return previous;
            const updated = { ...previous, subscription: fields.subscription.stringValue,
              subscriptionProvider: fields.subscriptionProvider?.stringValue || previous.subscriptionProvider };
            localStorage.setItem('finflow_user_profile', JSON.stringify(updated));
            return updated;
          });
        }
      }
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
    }
  }

  async function fetchUserProfile(user: User) {
    console.log('🔍 [AuthContext] Fetching user profile for:', user.uid);
    try {
      // Independent authenticated reads: keep server entitlement authority without
      // making startup wait for two network round trips in sequence.
      const [rootUserDoc, userDoc] = await Promise.all([
        readAuthProfile(user, true),
        readAuthProfile(user)
      ]);
      
      // Helper function to preserve original creation time
      const preserveCreationTime = (existingProfile: any, newProfile: any) => {
        if (existingProfile?.createdAt) {
          // If we have an existing profile with createdAt, preserve it
          return { ...newProfile, createdAt: existingProfile.createdAt };
        }
        // For new profiles, use current time
        return { ...newProfile, createdAt: Timestamp.now() };
      };
      
      if (userDoc.exists()) {
        const profileData = restoreSetupState(userDoc.data(), rootUserDoc.data());
        
        // Merge subscription status from root document if it exists
        if (rootUserDoc.exists()) {
          const rootData = rootUserDoc.data();
          if (rootData.subscription) {
            profileData.subscription = rootData.subscription;
          }
          if (rootData.subscriptionProvider) {
            profileData.subscriptionProvider = rootData.subscriptionProvider;
          }
          if (rootData.stripeCustomerId) {
            profileData.stripeCustomerId = rootData.stripeCustomerId;
          }
        }
        
        console.log('🔍 [AuthContext] Found existing profile:', profileData);
        
        // Store profile in localStorage as backup to prevent loss during race conditions
        localStorage.setItem('finflow_user_profile', JSON.stringify(profileData));
        
        setUserProfile(profileData as UserProfile);
        
      } else {
        console.log('🔍 [AuthContext] No profile found, creating default...');
        // Missing profile here is usually a legacy account; don't force onboarding.
        const defaultProfile: UserProfile = {
          name: user.displayName || user.email?.split('@')[0] || 'User',
          email: user.email || '',
          ageRange: '18-24',
          createdAt: Timestamp.now(),
          hasOnboarded: true,
          hasSeenOnboardingPrompt: true,
          subscription: rootUserDoc.data()?.subscription || 'free'
        };
        console.log('🔍 [AuthContext] Setting default profile:', defaultProfile);
        
        // Store in localStorage as backup
        localStorage.setItem('finflow_user_profile', JSON.stringify(defaultProfile));
        setUserProfile(defaultProfile);
        
        // Create profile in background
        setDoc(doc(db, 'users', user.uid, 'profile', 'main'), editableProfileFields(defaultProfile), { merge: true }).catch(error => {
          console.error('Failed to create default profile:', error);
        });
        setDoc(doc(db, 'users', user.uid), {
          email: defaultProfile.email,
          createdAt: defaultProfile.createdAt,
          hasOnboarded: defaultProfile.hasOnboarded,
          hasSeenOnboardingPrompt: defaultProfile.hasSeenOnboardingPrompt
        }, { merge: true }).catch(error => {
          console.error('Failed to seed root user doc from default profile:', error);
        });
      }
    } catch (error: any) {
      console.error('❌ [AuthContext] Error fetching user profile:', error);
      
      // Try to restore from localStorage backup first
      const backupProfile = localStorage.getItem('finflow_user_profile');
      if (backupProfile) {
        try {
          const restoredProfile = JSON.parse(backupProfile);
          console.log('🔍 [AuthContext] Restoring profile from localStorage backup:', restoredProfile);
          setUserProfile(restoredProfile as UserProfile);
          return; // Don't create a new default profile if we have a backup
        } catch (parseError) {
          console.error('❌ [AuthContext] Failed to parse backup profile:', parseError);
        }
      }
      
      // Avoid onboarding lockouts when profile fetch fails unexpectedly.
      const defaultProfile: UserProfile = {
        name: user.displayName || user.email?.split('@')[0] || 'User',
        email: user.email || '',
        ageRange: '18-24',
        createdAt: Timestamp.now(),
        hasOnboarded: true,
        hasSeenOnboardingPrompt: true,
        subscription: 'free'
      };
      console.log('🔍 [AuthContext] Setting fallback profile due to error:', defaultProfile);
      
      // Store in localStorage as backup
      localStorage.setItem('finflow_user_profile', JSON.stringify(defaultProfile));
      setUserProfile(defaultProfile);
    }
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    if (!currentUser?.email) throw new Error('No user logged in');
    
    try {
      // First verify the current password by attempting to sign in
      const verifyResponse = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${auth.app.options.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: currentUser.email,
          password: currentPassword,
          returnSecureToken: true
        })
      });

      const verifyData = await verifyResponse.json();
      if (verifyData.error) {
        throw new Error('Current password is incorrect');
      }

      // Now change the password using Firebase REST API
      const changeResponse = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:update?key=${auth.app.options.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          idToken: verifyData.idToken,
          password: newPassword,
          returnSecureToken: true
        })
      });

      const changeData = await changeResponse.json();
      if (changeData.error) {
        if (changeData.error.message === 'WEAK_PASSWORD') {
          throw new Error('New password should be at least 6 characters long');
        } else {
          throw new Error(changeData.error.message || 'Failed to change password');
        }
      }

      // Update stored auth data with new token
      const storedAuth = restAuthStorage.getItem('finflow_auth');
      if (storedAuth) {
        const authData = JSON.parse(storedAuth);
        authData.idToken = changeData.idToken;
        authData.refreshToken = changeData.refreshToken;
        restAuthStorage.setItem('finflow_auth', JSON.stringify(authData));
      }

      console.log('✅ Password changed successfully');
    } catch (error: any) {
      console.error('❌ Change password error:', error);
      throw new Error(error.message || 'Failed to change password');
    }
  }

  function checkAutoLogout() {
    // Simple logout function - no session management
    console.log('Manual logout triggered');
    logout();
  }



  // Listen for auth state changes
  useEffect(() => {
    // Auto-logout bridge from NativeFeatures when app inactive too long
    const handleAutoLogout = () => {
      try {
        console.log('🔒 [AuthContext] Auto-logout due to inactivity');
        void logout(false);
      } catch (e) {
        console.error('❌ [AuthContext] Auto-logout failed:', e);
      }
    };
    window.addEventListener('finflow-auto-logout', handleAutoLogout as EventListener);

    if (!auth) {
      setAuthLoading(false);
      return () => {
        window.removeEventListener('finflow-auto-logout', handleAutoLogout as EventListener);
      };
    }
    void completeGoogleRedirect(auth).then(async result=>{if(result?.user)await keepDeviceSession(result.user);}).catch(()=>{});
    
    // Set up a safer idle timeout (1 hour) that does not sign out if a valid local session exists
    const authTimeout = setTimeout(async () => {
      console.log('⏰ [AuthContext] Idle timeout reached, checking stored auth before sign-out...');
      const storedAuth = restAuthStorage.getItem('finflow_auth');
      if (!storedAuth) {
        try {
          await signOut(auth);
          console.log('✅ [AuthContext] User signed out due to idle timeout');
        } catch (error) {
          console.error('❌ [AuthContext] Error signing out on timeout:', error);
        }
        setCurrentUser(null);
        setUserProfile(null);
      } else {
        console.log('🔍 [AuthContext] Stored auth found; skipping sign-out on timeout');
      }
      // Don't force loading=false here if app is still mounting; let onAuthStateChanged finalize
    }, 3600000); // 1 hour in milliseconds
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // User auth state changed
      // Clear the timeout since we got a response
      clearTimeout(authTimeout);
      
      console.log('🔍 [AuthContext] Auth state changed:', user ? `User ${user.uid}` : 'No user');
      
      // If no Firebase Auth user, check for stored REST API authentication
      if (!user) {
        console.log('🔍 [AuthContext] No Firebase Auth user, checking for stored REST API auth...');
        const storedAuth = restAuthStorage.getItem('finflow_auth');
        
        if (storedAuth) {
          try {
            const authData = JSON.parse(storedAuth);
            authData.idToken = await getIdToken();
            console.log('🔍 [AuthContext] Found stored auth data:', authData.uid);
            
            // Check if token is still valid (not older than 1 hour)
            const tokenAge = Date.now() - authData.timestamp;
            if (authData.idToken) {
              // Create a Firebase-compatible user object from stored data
              const restoredUser = {
                uid: authData.uid,
                email: authData.email,
                displayName: authData.displayName,
                photoURL: null,
                emailVerified: false,
                isAnonymous: false,
                metadata: {
                  creationTime: new Date().toISOString(),
                  lastSignInTime: new Date().toISOString()
                },
                providerData: [{
                  uid: authData.uid,
                  email: authData.email,
                  displayName: authData.displayName,
                  photoURL: null,
                  providerId: 'password'
                }],
                refreshToken: authData.refreshToken,
                getIdToken: async () => getIdToken(),
                getIdTokenResult: async () => ({
                  authTime: new Date().toISOString(),
                  issuedAtTime: new Date().toISOString(),
                  signInProvider: 'password',
                  token: authData.idToken,
                  claims: {}
                })
              };
              
              console.log('🔍 [AuthContext] Restoring user from stored auth data');
              setCurrentUser(restoredUser as any);
              
              // Fetch user profile
              await fetchUserProfile(restoredUser as any);
              // Login bookkeeping must not hold the authenticated UI behind a
              // potentially cold API invocation. Auth/profile checks are complete.
              setAuthLoading(false);
              try {
                if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem('finflow_touch_login')) {
                  await fetch(buildApiUrl('/api/users/touch-login'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authData.idToken}` },
                    body: JSON.stringify({ uid: authData.uid })
                  });
                  sessionStorage.setItem('finflow_touch_login', '1');
                }
              } catch (_) { /* ignore */ }
            } else {
              console.log('🔍 [AuthContext] Stored token is too old, clearing auth data');
              restAuthStorage.removeItem('finflow_auth');
              setCurrentUser(null);
              setUserProfile(null);
            }
          } catch (error) {
            console.error('[AuthContext] Session restoration unavailable');
            setCurrentUser(null);
            setUserProfile(null);
            setAuthLoading(false);
          }
        } else {
          console.log('🔍 [AuthContext] No stored auth data found');
          setCurrentUser(null);
          setUserProfile(null);
          setAuthLoading(false);
        }
      } else {
        console.log('🔍 [AuthContext] Firebase Auth user found, fetching profile...');
        setCurrentUser(user);
        await fetchUserProfile(user);
        setAuthLoading(false);
        try {
          if (typeof sessionStorage !== 'undefined' && !sessionStorage.getItem('finflow_touch_login')) {
            const token = await user.getIdToken();
            await fetch(buildApiUrl('/api/users/touch-login'), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ uid: user.uid })
            });
            sessionStorage.setItem('finflow_touch_login', '1');
          }
        } catch (_) { /* ignore */ }
        setAuthLoading(false);
      }
      // authLoading finalized above for each branch
    }, (error: any) => {
      console.error('❌ Auth state change error:', error);
      
      // Handle session expiration or auth errors gracefully
      if (error.code === 'auth/user-token-expired' || 
          error.code === 'auth/user-disabled' || 
          error.code === 'auth/invalid-credential' ||
          error.message?.includes('token') ||
          error.message?.includes('expired')) {
        console.log('🔍 [AuthContext] Session expired, signing out user immediately...');
        
        // Immediately sign out without showing loading screen
        signOut(auth).catch(signOutError => {
          console.error('❌ [AuthContext] Error signing out after auth error:', signOutError);
        });
        
        // Set states immediately
        setCurrentUser(null);
        setUserProfile(null);
        setAuthLoading(false);
      }
    });

    return () => {
      window.removeEventListener('finflow-auto-logout', handleAutoLogout as EventListener);
      clearTimeout(authTimeout);
      unsubscribe();
    };
  }, [auth, db]);



  const value = {
    currentUser,
    userProfile,
    authLoading,
    signup,
    login,
    loginWithGoogle,
    loginWithApple,
    logout,
    changePassword,
    changeEmail,
    updateUserProfile,
    refreshUserProfile,
    checkAutoLogout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
